/**
 * @fileoverview Sidebar principal da aplicação (desktop e mobile).
 *
 * Contém:
 * - Logo e seletor de projeto (ProjectSwitcher)
 * - Navegação organizada por seções (CRM, Tasks ou Funil)
 * - Favoritos e itens recentes
 * - Atalhos de teclado exibidos nos tooltips
 * - Filtro de busca por label
 * - Footer com avatar do usuário e botão de logout
 *
 * No mobile, a sidebar é renderizada dentro de um Sheet (drawer).
 * No desktop, suporta modo colapsado (ícones apenas) e expandido.
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Kanban, Users, Settings, Moon, Sun,
  TrendingUp, UsersRound, MessageCircle, Briefcase, Target,
  Lightbulb, X, CheckSquare, Layers, BookOpen, Milestone,
  BarChart3, Star, ChevronDown, LogOut, FlaskConical, FolderKanban,
  Database, FileText, Activity, Timer, Code2, Heart, Zap, Mail,
  ClipboardList, Clock, Calendar, Brain, DollarSign, Search,
} from 'lucide-react';
import { useStore } from '@/store/useStore';
import { useShallow } from 'zustand/shallow';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useNavPreferences } from '@/hooks/useNavPreferences';
import { cn } from '@/lib/utils';
import logoDark from '@/assets/logo-light.svg';
import logoWhite from '@/assets/logo-dark.svg';
import logoIcon from '@/assets/logo-icon.png';
// Removed framer-motion — active indicator uses CSS instead
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/** Mapeamento de tipo de projeto para label exibido no seletor */
const PROJECT_TYPE_LABELS: Record<string, string> = { crm: 'CRM', tasks: 'Gestão', funnel: 'Funil' };

/** Seletor de projetos dentro do workspace ativo. Oculta o dropdown se há apenas 1 projeto. */
function ProjectSwitcher() {
  const { activeWorkspaceId, workspaces, activeProjectId, setActiveProject } = useStore(useShallow((s) => ({
    activeWorkspaceId: s.activeWorkspaceId,
    workspaces: s.workspaces,
    activeProjectId: s.activeProjectId,
    setActiveProject: s.setActiveProject,
  })));
  const workspace = workspaces.find(w => w.id === activeWorkspaceId);
  const projects = workspace?.projects || [];
  const activeProject = projects.find(p => p.id === activeProjectId);

  if (projects.length <= 1) {
    // Only one project — show name but no dropdown
    return activeProject ? (
      <div className="px-3 pb-2">
        <div className="flex items-center gap-2 px-2 py-1 text-xs text-sidebar-muted">
          <FolderKanban className="h-3 w-3 shrink-0" />
          <span className="truncate font-medium text-sidebar-foreground">{activeProject.name}</span>
        </div>
      </div>
    ) : null;
  }

  return (
    <div className="px-3 pb-2">
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors">
            <FolderKanban className="h-3 w-3 shrink-0 text-secondary" />
            <span className="truncate font-medium flex-1 text-left">{activeProject?.name || 'Selecionar projeto'}</span>
            <ChevronDown className="h-3 w-3 text-sidebar-muted shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent side="bottom" align="start" className="w-52 p-1">
          {projects.map(p => (
            <button
              key={p.id}
              onClick={() => setActiveProject(p.id, p.type)}
              className={cn(
                "flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-xs transition-colors text-left",
                p.id === activeProjectId
                  ? "bg-secondary/10 text-secondary font-semibold"
                  : "text-foreground hover:bg-muted"
              )}
            >
              <FolderKanban className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate flex-1">{p.name}</span>
              <span className="text-2xs text-muted-foreground">{PROJECT_TYPE_LABELS[p.type] || p.type}</span>
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}

const CRM_NAV_SECTIONS = [
  {
    label: 'Principal',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { label: 'Atividades', path: '/atividades', icon: Activity },
    ],
  },
  {
    label: 'Vendas',
    items: [
      { label: 'Pipeline', path: '/pipeline', icon: Kanban },
      { label: 'Leads', path: '/leads', icon: Users },
      { label: 'Clientes', path: '/clientes', icon: Briefcase },
      { label: 'Forecast', path: '/previsao', icon: Target },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { label: 'Highlights', path: '/highlights', icon: Lightbulb },
      { label: 'Metas & OKRs', path: '/metas', icon: Target },
      { label: 'Relatórios', path: '/relatorios', icon: BarChart3 },
      { label: 'Retenção', path: '/retencao', icon: Heart },
      { label: 'NPS', path: '/nps', icon: Star },
    ],
  },
  {
    label: 'Operacional',
    items: [
      { label: 'Equipe', path: '/equipe', icon: UsersRound },
      { label: 'Financeiro', path: '/financeiro', icon: DollarSign, roles: ['financeiro', 'admin'] },
      { label: 'Agendamentos', path: '/agendamentos', icon: Calendar },
      // { label: 'Formulários', path: '/formularios', icon: FileText },
    ],
  },
  {
    label: 'Automação',
    roles: ['admin', 'manager'],
    items: [
      { label: 'Automações', path: '/automacoes', icon: Zap },
      { label: 'WhatsApp', path: '/whatsapp', icon: MessageCircle },
      { label: 'Treinamento IA', path: '/ai-knowledge', icon: Brain },
    ],
  },
];

// Build CRM (crm.blacklegacy.com.br): TASKS_NAV_SECTIONS e FUNNEL_NAV_SECTIONS
// foram removidos junto com os tipos `tasks` e `funnel`. Apenas CRM_NAV_SECTIONS
// é renderizado nesta build.

const COMMON_NAV = [
  { label: 'Workspaces', path: '/workspaces', icon: Layers },
  { label: 'Configurações', path: '/configuracoes', icon: Settings },
];

interface AppSidebarProps {
  collapsed: boolean;
  isMobile: boolean;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
  isDark: boolean;
  userName: string;
  userEmail: string;
  userAvatar: string;
  activeProjectType: string | null;
  handleLogout: () => void;
}

/**
 * Componente principal da sidebar. Renderiza navegação por seções,
 * favoritos, itens comuns e footer com perfil do usuário.
 * Filtra itens de menu por role do usuário (RBAC).
 */
export function AppSidebar({
  collapsed,
  isMobile,
  mobileOpen,
  setMobileOpen,
  isDark,
  userName,
  userEmail,
  userAvatar,
  activeProjectType,
  handleLogout,
}: AppSidebarProps) {
  const location = useLocation();
  const { favorites, recent, toggleFavorite, addRecent } = useNavPreferences();
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const { userProfile } = useStore();
  const [sidebarSearchState, setSidebarSearchState] = useState('');
  const hasSearch = sidebarSearchState.trim().length > 0;
  // O(1) favorite lookup; the underlying array recomputes only when the
  // user toggles a star, so the Set rebuilds rarely.
  const favoritesSet = useMemo(() => new Set(favorites), [favorites]);

  const userRoleStr = userProfile?.role || '';
  const userRoles = userRoleStr.split(',').map(r => r.trim().toLowerCase()).filter(Boolean);
  // Only show role-restricted items after profile loads. During loading, show common items only.
  const profileLoaded = userRoles.length > 0;
  const isSuperUser = profileLoaded && (userRoles.includes('admin') || userRoles.includes('owner') || userRoles.includes('ceo'));

  const hasAccess = (rolesMatch?: string[]) => {
    if (!rolesMatch || rolesMatch.length === 0) return true;
    if (isSuperUser) return true;
    return rolesMatch.some(r => userRoles.includes(r.toLowerCase()));
  };

  const filterNavByRole = (sections: any[]) => {
    return sections
      .filter(section => hasAccess(section.roles))
      .map(section => ({
        ...section,
        items: section.items.filter((item: any) => hasAccess(item.roles))
      }))
      .filter(section => section.items.length > 0);
  };

  const NAV_SECTIONS = filterNavByRole(CRM_NAV_SECTIONS);
  const FILTERED_COMMON_NAV = COMMON_NAV.filter((item: any) => hasAccess(item.roles));

  // Keyboard shortcut map (matches AppLayout Cmd+1-5 registration)
  const SHORTCUT_MAP: Record<string, string> = useMemo(() => {
    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
    const mod = isMac ? '\u2318' : 'Ctrl+';
    return {
      '/dashboard': `${mod}1`,
      '/pipeline': `${mod}2`,
      '/leads': `${mod}3`,
      '/clientes': `${mod}4`,
      '/configuracoes': `${mod}5`,
    };
  }, []);

  const ALL_NAV = [...NAV_SECTIONS.flatMap(s => s.items), ...FILTERED_COMMON_NAV];
  const favoriteItems = ALL_NAV.filter(item => favorites.includes(item.path));
  const recentItems = recent.map(path => ALL_NAV.find(i => i.path === path)).filter(Boolean) as typeof ALL_NAV;

  useEffect(() => {
    setMobileOpen(false);
    addRecent(location.pathname);
  }, [location.pathname]);

  const toggleSection = (label: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(label)) { next.delete(label); } else { next.add(label); };
      return next;
    });
  };

  // Single source of truth for "is this nav item the active route?". Used
  // by both the expanded nav-item (with hover+favorite affordance) and the
  // collapsed icon-only mode below — keeps highlight behavior consistent
  // across both modes (previously collapsed used exact-match only, missing
  // nested routes like `/leads/123`).
  const isItemActive = (path: string) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));

  // NavItem uses render props via closure — extracted logic keeps stable references
  const renderNavItem = (item: typeof ALL_NAV[0], showFavorite = false) => {
    const active = isItemActive(item.path);
    const isFav = favoritesSet.has(item.path);
    return (
      <div key={item.path} className="group relative">
        <Link
          to={item.path}
          className={cn(
            "flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-200 relative overflow-hidden",
            active
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : "text-sidebar-muted hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
          )}
        >
          {active && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-secondary transition-all duration-200" />
          )}
          <item.icon className={cn("h-4 w-4 shrink-0 transition-all", active ? "text-secondary" : "group-hover:text-foreground")} />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {active && <div className="absolute inset-0 bg-secondary/5 rounded-lg pointer-events-none" />}
        </Link>
        {showFavorite && !collapsed && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.path); }}
            aria-label={isFav ? `Remover ${item.label} dos favoritos` : `Adicionar ${item.label} aos favoritos`}
            className={cn(
              "absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity",
              isFav && "opacity-100"
            )}
          >
            <Star className={cn("h-3 w-3", isFav ? "fill-warning text-warning" : "text-muted-foreground hover:text-warning")} />
          </button>
        )}
      </div>
    );
  };

  const SidebarNav = () => {
    const searchLower = sidebarSearchState.toLowerCase();
    const filteredSections = hasSearch
      ? NAV_SECTIONS.map(s => ({ ...s, items: s.items.filter((i: any) => i.label.toLowerCase().includes(searchLower)) })).filter(s => s.items.length > 0)
      : NAV_SECTIONS;
    const filteredCommon = hasSearch
      ? FILTERED_COMMON_NAV.filter((i: any) => i.label.toLowerCase().includes(searchLower))
      : FILTERED_COMMON_NAV;

    return (
      <>
        {/* Inline filter — only useful when expanded; collapsed mode hides
            the bar to keep the icon strip clean. Esc clears the query. */}
        {!collapsed && (
          <div className="px-3 pt-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-sidebar-muted pointer-events-none" />
              <input
                type="search"
                value={sidebarSearchState}
                onChange={(e) => setSidebarSearchState(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape' && hasSearch) { e.preventDefault(); setSidebarSearchState(''); } }}
                placeholder="Filtrar menu…"
                aria-label="Filtrar itens do menu"
                className={cn(
                  "w-full h-7 pl-7 text-xs rounded-md bg-sidebar-accent/30 border border-sidebar-border/40",
                  "text-sidebar-foreground placeholder:text-sidebar-muted",
                  "focus:outline-none focus:ring-1 focus:ring-secondary/40",
                  hasSearch ? 'pr-7' : 'pr-2',
                )}
              />
              {hasSearch && (
                <button
                  type="button"
                  onClick={() => setSidebarSearchState('')}
                  aria-label="Limpar filtro"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded text-sidebar-muted hover:text-sidebar-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        )}

        <ScrollArea className="flex-1">
          <nav aria-label="Main navigation" className="py-3 px-2 space-y-0.5">
            {favoriteItems.length > 0 && !collapsed && !hasSearch && (
              <div className="mb-3">
                <div className="flex items-center gap-1.5 px-3 mb-1.5">
                  <Star className="h-3 w-3 text-warning fill-warning" />
                  <span className="text-2xs font-bold uppercase tracking-wider text-sidebar-muted">Favoritos</span>
                </div>
                {favoriteItems.map((item: any) => renderNavItem(item))}
                <Separator className="my-2 bg-sidebar-border" />
              </div>
            )}

            {filteredSections.map(section => (
              <Collapsible key={section.label} defaultOpen={!collapsedSections.has(section.label)} onOpenChange={() => toggleSection(section.label)}>
                {!collapsed && (
                  <CollapsibleTrigger className="flex items-center gap-1.5 px-3 py-1 w-full text-left group/section">
                    <span className="text-2xs font-bold uppercase tracking-wider text-sidebar-muted flex-1">{section.label}</span>
                    <ChevronDown className="h-3 w-3 text-sidebar-muted transition-transform group-data-[state=closed]/section:-rotate-90" />
                  </CollapsibleTrigger>
                )}
                <CollapsibleContent className="space-y-0.5">
                  {section.items.map((item: any) => {
                    const active = isItemActive(item.path);
                    return collapsed ? (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>
                          <Link to={item.path} className={cn(
                            "flex items-center justify-center rounded-lg px-3 py-2 transition-all relative",
                            active ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-sidebar-muted hover:bg-sidebar-accent/50"
                          )}>
                            {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-secondary" />}
                            <item.icon className={cn("h-4.5 w-4.5", active && "text-secondary")} />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="text-xs">
                          <span className="flex items-center gap-2">
                            {item.label}
                            {SHORTCUT_MAP[item.path] && (
                              <kbd className="ml-1 inline-flex items-center rounded bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground border border-border/50">
                                {SHORTCUT_MAP[item.path]}
                              </kbd>
                            )}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      renderNavItem(item, true)
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            ))}

            {!collapsed && <Separator className="my-2 bg-sidebar-border" />}

            {filteredCommon.map(item => {
              const active = isItemActive(item.path);
              return collapsed ? (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <Link to={item.path} className={cn(
                      "flex items-center justify-center rounded-lg px-3 py-2 transition-all relative",
                      active ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-sidebar-muted hover:bg-sidebar-accent/50"
                    )}>
                      <item.icon className={cn("h-4.5 w-4.5", active && "text-secondary")} />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs">
                    <span className="flex items-center gap-2">
                      {item.label}
                      {SHORTCUT_MAP[item.path] && (
                        <kbd className="ml-1 inline-flex items-center rounded bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground border border-border/50">
                          {SHORTCUT_MAP[item.path]}
                        </kbd>
                      )}
                    </span>
                  </TooltipContent>
                </Tooltip>
              ) : (
                renderNavItem(item)
              );
            })}

            {hasSearch && filteredSections.length === 0 && filteredCommon.length === 0 && (
              <p className="text-xs text-sidebar-muted text-center py-4">Nenhum item para "{sidebarSearchState}"</p>
            )}
          </nav>
        </ScrollArea>
      </>
    );
  };

  const SidebarFooter = () => (
    <div className="border-t border-sidebar-border p-3">
      <div className={cn("flex items-center", collapsed ? "flex-col gap-2" : "gap-3")}>
        <UserAvatar name={userName} avatarUrl={userAvatar} size="md" className="ring-2 ring-secondary/20" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-sidebar-foreground truncate">{userName}</p>
            <p className="text-xs text-sidebar-muted truncate">{userEmail}</p>
          </div>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-sidebar-muted hover:text-destructive shrink-0" onClick={handleLogout} aria-label="Sair">
              <LogOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side={collapsed ? "right" : "top"} className="text-xs">Sair</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );

  const sidebarContent = (
    <>
      <SidebarNav />
      <SidebarFooter />
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      {!isMobile && (
        <aside className={cn(
          "flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300 shrink-0",
          collapsed ? "w-16" : "w-56"
        )}>
          <div className="border-b border-sidebar-border">
            <div className="flex h-14 items-center justify-center px-3">
              {collapsed ? (
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              ) : (
                <img src={isDark ? logoWhite : logoDark} alt="Logo" className="h-7 object-contain ml-1" />
              )}
            </div>
            {!collapsed && <ProjectSwitcher />}
          </div>
          {sidebarContent}
        </aside>
      )}

      {/* Mobile sheet (controlled, no SheetTrigger) */}
      {isMobile && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-60 p-0 bg-sidebar border-sidebar-border flex flex-col">
            <div className="border-b border-sidebar-border">
              <div className="flex h-14 items-center px-4">
                <img src={isDark ? logoWhite : logoDark} alt="Logo" className="h-7 object-contain" />
              </div>
              {/* Mobile parity with desktop sidebar — users on phones with
                  multi-project workspaces previously had to leave the drawer
                  to switch projects. */}
              <ProjectSwitcher />
            </div>
            {sidebarContent}
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
